import React, { useEffect, useState } from 'react';
import { BarChart3, Download, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { Customer } from '../types/index.js';

export const Usage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.getCustomers();
        setCustomers(res.items || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleExportCSV = () => {
    const headers = ['Cliente', 'Email', 'Projetos', 'Aulas Produzidas', 'Status'];
    const rows = customers.map((c) => [
      c.tradeName || c.legalName || c.fullName,
      c.email,
      c.totalProjects || 0,
      c.totalLessons || 0,
      c.status,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `edurender_relatorio_utilizacao_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Header
        title="Dashboard de Utilização Master"
        subtitle="Monitoramento granular do consumo de franquias por cliente com alertas preventivos de esgotamento"
        action={
          <EduButton
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            icon={<Download size={14} />}
          >
            Exportar CSV
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <EduCard>
            <div className="flex items-center justify-between">
              <span className="text-xs text-txt-secondary">Franquias em Uso Saudável</span>
              <span className="p-2 bg-green-50 text-status-success rounded-lg">
                <CheckCircle size={16} />
              </span>
            </div>
            <span className="text-3xl font-bold text-txt-primary mt-2 block">
              {customers.length} clientes
            </span>
            <span className="text-xs text-txt-muted mt-1 block">
              Consumo dentro do planejado
            </span>
          </EduCard>

          <EduCard>
            <div className="flex items-center justify-between">
              <span className="text-xs text-txt-secondary">Alerta de Consumo (&gt;80%)</span>
              <span className="p-2 bg-amber-50 text-status-warning rounded-lg">
                <AlertTriangle size={16} />
              </span>
            </div>
            <span className="text-3xl font-bold text-status-warning mt-2 block">0</span>
            <span className="text-xs text-txt-muted mt-1 block">
              Oportunidade para oferta de upgrade
            </span>
          </EduCard>

          <EduCard>
            <div className="flex items-center justify-between">
              <span className="text-xs text-txt-secondary">Franquia Esgotada (100%)</span>
              <span className="p-2 bg-red-50 text-status-error rounded-lg">
                <AlertCircle size={16} />
              </span>
            </div>
            <span className="text-3xl font-bold text-status-error mt-2 block">0</span>
            <span className="text-xs text-txt-muted mt-1 block">
              Sem saldo restante no ciclo
            </span>
          </EduCard>
        </div>

        {/* Utilization Table */}
        <EduCard title="Tabela Geral de Utilização por Tenant">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-bdr text-txt-secondary font-semibold bg-surface-secondary">
                  <th className="py-3 px-4">Cliente</th>
                  <th className="py-3 px-4">Plano</th>
                  <th className="py-3 px-4">Aulas Totais</th>
                  <th className="py-3 px-4">Projetos</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Renovação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bdr text-txt-primary">
                {customers.map((c) => (
                  <tr key={c.id} className="hover:bg-surface-secondary/40">
                    <td className="py-3 px-4 font-semibold text-txt-primary">
                      {c.tradeName || c.legalName || c.fullName}
                    </td>
                    <td className="py-3 px-4 font-medium text-brand-primary">
                      {c.planId || 'Plano Pro'}
                    </td>
                    <td className="py-3 px-4 font-bold">{c.totalLessons || 0}</td>
                    <td className="py-3 px-4">{c.totalProjects || 0}</td>
                    <td className="py-3 px-4">
                      {c.status === 'active' ? (
                        <EduBadge variant="success" size="sm" dot>Ativo</EduBadge>
                      ) : (
                        <EduBadge variant="error" size="sm" dot>Suspenso</EduBadge>
                      )}
                    </td>
                    <td className="py-3 px-4 font-mono text-txt-secondary">
                      {c.nextRenewal ? new Date(c.nextRenewal).toLocaleDateString('pt-BR') : 'Mensal'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EduCard>
      </div>
    </div>
  );
};
